import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from "@/components/ui/select";
import AutocompleteInput from "@/components/shared/AutocompleteInput";
import { cn } from "@/lib/utils";
import { useEffect, useState, useRef } from "react";
import { AlertTriangle } from "lucide-react";

function UnitBox({ unit }: { unit: string }) {
  return (
    <div className="h-7 px-2 bg-muted border border-l-0 rounded-l-none rounded-r-md flex items-center justify-center">
      <span className="text-xs text-muted-foreground font-medium">{unit}</span>
    </div>
  );
}

function InputWithUnit({
  placeholder,
  unit,
  value,
  onChange,
  className = "",
  title
}: {
  placeholder?: string;
  unit: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  title?: string;
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Prevent negative values
    if (inputValue === '' || (parseFloat(inputValue) >= 0 && !isNaN(parseFloat(inputValue)))) {
      onChange(inputValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent minus key, 'e', and 'E' (for exponential notation)
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  return (
    <div className="flex">
      <Input
        type="number"
        min="0"
        step="any"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        title={title}
        className={cn("h-7 rounded-r-none border-r-0 focus-visible:ring-0 focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none", className)}
      />
      <UnitBox unit={unit} />
    </div>
  );
}

// Clinical observation options
const CLINICAL_OPTIONS = [
  "None", 
  "Mild", 
  "Moderate", 
  "Severe"
];

export default function VitalsForm() {
  const {
    setValue,
    watch,
    formState: { errors }
  } = useFormContext();

  // Watch vitals
  const vitals = watch("vitals") || {};
  
  // BP state management - separate inputs, combined storage
  const [bpSys, setBpSys] = useState("");
  const [bpDia, setBpDia] = useState("");

  // Initialize BP from combined string
  useEffect(() => {
    const currentBP = vitals.bp || "";
    
    // Only update if the combined BP is different from what we expect
    const expectedBP = bpSys && bpDia ? `${bpSys}/${bpDia}` : 
                      bpSys ? bpSys : 
                      bpDia ? `/${bpDia}` : "";
    
    if (currentBP !== expectedBP) {
      if (currentBP.includes("/")) {
        const [sys, dia] = currentBP.split("/");
        setBpSys(sys || "");
        setBpDia(dia || "");
      } else {
        // If it's a single value and we don't have sys/dia set, it might be initial data
        if (!bpSys && !bpDia && currentBP) {
          setBpSys(currentBP);
          setBpDia("");
        } else if (!currentBP) {
          // Only clear if BP is actually empty
          setBpSys("");
          setBpDia("");
        }
      }
    }
  }, [vitals.bp]);

  // Update combined BP when individual values change
  const updateBP = (sys: string, dia: string) => {
    // Don't clean the values here - let the user type normally
    let combinedBP = "";
    if (sys && dia) {
      combinedBP = `${sys}/${dia}`;
    } else if (sys) {
      combinedBP = sys;
    } else if (dia) {
      combinedBP = `/${dia}`;
    }
    
    setValue("vitals.bp", combinedBP, { shouldDirty: true });
  };

  // Handle BP changes - with negative value prevention
  const handleBpSysChange = (value: string) => {
    // Prevent negative values
    if (value === '' || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
      setBpSys(value);
      updateBP(value, bpDia);
    }
  };

  const handleBpDiaChange = (value: string) => {
    // Prevent negative values
    if (value === '' || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
      setBpDia(value);
      updateBP(bpSys, value);
    }
  };

  // Handle other vital changes - with negative value prevention
  const handleValueChange = (field: string, value: string) => {
    // Prevent negative values for numeric vital signs
    if (value === '' || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
      setValue(`vitals.${field}`, value, { shouldDirty: true });
    }
  };

  // Handle select changes
  const handleSelectChange = (field: string, value: string) => {
    setValue(`vitals.${field}`, value, { shouldDirty: true });
  };

  // Handle keyboard input for BP fields
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent minus key, 'e', and 'E' (for exponential notation)
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  return (
    <div className="space-y-3">
      {/* Row 1: Main Vitals */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        
        {/* Blood Pressure */}
        <div className="col-span-2">
          <label className="block mb-1 text-sm font-medium text-foreground">
            Blood Pressure
            <span className="text-xs text-muted-foreground font-normal ml-1">(Systolic/Diastolic)</span>
          </label>
          <div className="flex">
            <Input
              type="number"
              min="0"
              placeholder="120"
              value={bpSys}
              onChange={(e) => handleBpSysChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-7 w-16 rounded-r-none border-r-0 focus-visible:ring-0 focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              title="Systolic blood pressure"
            />
            <div className="w-3 h-7 flex items-center justify-center text-sm font-medium text-muted-foreground bg-muted border-t border-b">
              /
            </div>
            <Input
              type="number"
              min="0"
              placeholder="80"
              value={bpDia}
              onChange={(e) => handleBpDiaChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-7 w-16 rounded-none border-l-0 border-r-0 focus-visible:ring-0 focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              title="Diastolic blood pressure"
            />
            <UnitBox unit="mmHg" />
          </div>
        </div>

        {/* Pulse */}
        <div>
          <label className="block mb-1 text-sm font-medium text-foreground">Pulse</label>
          <InputWithUnit 
            placeholder="72" 
            unit="bpm"
            value={vitals.pulse || ""}
            onChange={(value) => handleValueChange("pulse", value)}
            title="Heart rate"
          />
        </div>

        {/* SPO2 */}
        <div>
          <label className="block mb-1 text-sm font-medium text-foreground">SPO2</label>
          <InputWithUnit 
            placeholder="99" 
            unit="%"
            value={vitals.spo2 || ""}
            onChange={(value) => handleValueChange("spo2", value)}
            title="Oxygen saturation"
          />
        </div>

        {/* Weight */}
        <div>
          <label className="block mb-1 text-sm font-medium text-foreground">Weight</label>
          <InputWithUnit 
            placeholder="65" 
            unit="kg"
            value={vitals.weight || ""}
            onChange={(value) => handleValueChange("weight", value)}
            title="Patient weight"
          />
        </div>

        {/* Temperature */}
        <div>
          <label className="block mb-1 text-sm font-medium text-foreground">Temperature</label>
          <InputWithUnit 
            placeholder="98.4" 
            unit="°F"
            value={vitals.temp || ""}
            onChange={(value) => handleValueChange("temp", value)}
            title="Body temperature"
          />
        </div>
      </div>

      {/* Row 2: Clinical Observations */}
      <div>
        <h4 className="text-sm font-medium text-foreground mb-2">Clinical Observations</h4>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 max-w-2xl">
          
          {/* Edema */}
          <div className="col-span-1 md:col-span-2">
            <label className="block mb-1 text-sm font-medium text-muted-foreground">Edema</label>
            <AutocompleteInput
              value={vitals.edema || ""}
              onChange={(val) => handleSelectChange("edema", val)}
              options={CLINICAL_OPTIONS}
              placeholder="Select..."
              className="h-7 text-sm"
              showAllOnFocus={true}
            />
          </div>

          {/* Icterus */}
          <div className="col-span-1 md:col-span-2">
            <label className="block mb-1 text-sm font-medium text-muted-foreground">Icterus</label>
            <AutocompleteInput
              value={vitals.icterus || ""}
              onChange={(val) => handleSelectChange("icterus", val)}
              options={CLINICAL_OPTIONS}
              placeholder="Select..."
              className="h-7 text-sm"
              showAllOnFocus={true}
            />
          </div>

          {/* Pallor */}
          <div className="col-span-1 md:col-span-2">
            <label className="block mb-1 text-sm font-medium text-muted-foreground">Pallor</label>
            <AutocompleteInput
              value={vitals.pallor || ""}
              onChange={(val) => handleSelectChange("pallor", val)}
              options={CLINICAL_OPTIONS}
              placeholder="Select..."
              className="h-7 text-sm"
              showAllOnFocus={true}
            />
          </div>
        </div>
      </div>

      {/* Form validation errors */}
      {errors.vitals && (
        <div className="text-sm text-destructive flex items-center gap-2 mt-2 p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>Please check vital signs - ensure values are valid numbers</span>
        </div>
      )}
    </div>
  );
}